import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { clsx } from 'clsx';
import { X, AlertTriangle, Info, CheckCircle } from 'lucide-react';
import { Button } from './Button';

// Props para Modal base
interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  closeOnBackdrop?: boolean;
  showCloseButton?: boolean;
}

// Props para Dialog
interface DialogProps extends Omit<ModalProps, 'children'> {
  title: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
}

// Props para ConfirmDialog
interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  severity?: 'info' | 'warning' | 'danger' | 'success';
  loading?: boolean;
}

/**
 * Backdrop para modais
 */
const Backdrop: React.FC<{ onClick?: () => void }> = ({ onClick }) => (
  <div 
    className="fixed inset-0 bg-black bg-opacity-50 z-40 animate-fade-in"
    onClick={onClick}
  />
);

/**
 * Modal base seguindo o design system
 */
export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  children,
  size = 'md',
  closeOnBackdrop = true,
  showCloseButton = true
}) => {
  // Fechar com ESC
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg', 
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    full: 'max-w-full mx-4'
  };

  const modal = (
    <>
      <Backdrop onClick={closeOnBackdrop ? onClose : undefined} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className={clsx(
          'relative w-full bg-white rounded-lg shadow-xl animate-slide-up',
          sizeClasses[size]
        )}>
          {showCloseButton && (
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-1 text-slate-400 hover:text-slate-600 transition-colors z-10"
            >
              <X size={20} />
            </button>
          )}
          {children}
        </div>
      </div>
    </>
  );

  return createPortal(modal, document.body);
};

/**
 * Dialog com header, content e footer
 */
export const Dialog: React.FC<DialogProps> = ({
  title,
  children,
  footer,
  ...modalProps
}) => {
  return (
    <Modal {...modalProps}>
      <div className="px-6 py-4 border-b border-slate-200">
        <h2 className="text-h3">{title}</h2>
      </div>
      
      <div className="px-6 py-4 max-h-96 overflow-y-auto custom-scrollbar">
        {children}
      </div>
      
      {footer && (
        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-lg">
          {footer}
        </div>
      )}
    </Modal>
  );
};

/**
 * Dialog de confirmação seguindo o design system
 */
export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  severity = 'warning',
  loading = false
}) => {
  const handleConfirm = () => {
    onConfirm();
  };

  const severityConfig = {
    info: {
      icon: Info,
      iconColor: 'text-blue-600',
      bgColor: 'bg-blue-50',
      buttonVariant: 'secondary' as const
    },
    warning: {
      icon: AlertTriangle,
      iconColor: 'text-amber-600', 
      bgColor: 'bg-amber-50',
      buttonVariant: 'secondary' as const
    },
    danger: {
      icon: AlertTriangle,
      iconColor: 'text-red-600',
      bgColor: 'bg-red-50', 
      buttonVariant: 'danger' as const
    },
    success: {
      icon: CheckCircle,
      iconColor: 'text-green-600',
      bgColor: 'bg-green-50',
      buttonVariant: 'success' as const
    }
  };

  const config = severityConfig[severity];
  const IconComponent = config.icon;

  const footer = (
    <div className="flex justify-end gap-3">
      <Button
        variant="outline"
        onClick={onClose}
        disabled={loading}
      >
        {cancelText}
      </Button>
      <Button
        variant={config.buttonVariant}
        onClick={handleConfirm}
        loading={loading}
      >
        {confirmText}
      </Button>
    </div>
  );

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      footer={footer}
      size="sm"
      closeOnBackdrop={!loading}
      showCloseButton={!loading}
    >
      <div className="flex gap-4">
        <div className={clsx(
          'flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center',
          config.bgColor
        )}>
          <IconComponent className={config.iconColor} size={20} />
        </div>
        <div className="flex-1">
          <p className="text-body text-slate-700">
            {message}
          </p>
        </div>
      </div>
    </Dialog>
  );
};

// Props para Alert
interface AlertProps {
  variant?: 'info' | 'success' | 'warning' | 'error';
  title?: string;
  children: React.ReactNode;
  onClose?: () => void;
  className?: string;
}

/**
 * Componente Alert para notificações
 */
export const Alert: React.FC<AlertProps> = ({
  variant = 'info',
  title,
  children,
  onClose,
  className
}) => {
  const variantConfig = {
    info: {
      icon: Info,
      bgColor: 'bg-blue-50 border-blue-200',
      iconColor: 'text-blue-600',
      titleColor: 'text-blue-800',
      textColor: 'text-blue-700'
    },
    success: {
      icon: CheckCircle,
      bgColor: 'bg-green-50 border-green-200',
      iconColor: 'text-green-600',
      titleColor: 'text-green-800',
      textColor: 'text-green-700'
    },
    warning: {
      icon: AlertTriangle,
      bgColor: 'bg-amber-50 border-amber-200',
      iconColor: 'text-amber-600',
      titleColor: 'text-amber-800',
      textColor: 'text-amber-700'
    },
    error: {
      icon: AlertTriangle,
      bgColor: 'bg-red-50 border-red-200',
      iconColor: 'text-red-600',
      titleColor: 'text-red-800',
      textColor: 'text-red-700'
    }
  };

  const config = variantConfig[variant];
  const IconComponent = config.icon;

  return (
    <div className={clsx(
      'border rounded-lg p-4',
      config.bgColor,
      className
    )}>
      <div className="flex">
        <div className="flex-shrink-0">
          <IconComponent className={config.iconColor} size={20} />
        </div>
        <div className="ml-3 flex-1">
          {title && (
            <h3 className={clsx('text-sm font-medium', config.titleColor)}>
              {title}
            </h3>
          )}
          <div className={clsx(
            'text-sm',
            config.textColor,
            title && 'mt-1'
          )}>
            {children}
          </div>
        </div>
        {onClose && (
          <div className="ml-auto pl-3">
            <button
              onClick={onClose}
              className={clsx(
                'inline-flex rounded-md p-1.5 transition-colors',
                config.iconColor,
                'hover:bg-black hover:bg-opacity-10'
              )}
            >
              <X size={16} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};